import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    FinalizeInvoicePostModel,
    InsertAdjustmentPostModel,
    InsertInvoicePostModel,
    InsertPotentialEntryPostModel,
    UpdateInvoicePostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import moment from "moment";
import { toast } from "react-toastify";

interface Props {
    start?: Date;
    end?: Date;
    assignmentId?: number;
    finalizeOnly?: boolean;
    id?: number;
    invoiceId?: number;
    categoryId?: number;
}

export default function useInvoiceManagementData({
    start,
    end,
    assignmentId,
    finalizeOnly,
    id,
    categoryId,
}: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getInvoiceQuery = useQuery({
        queryKey: [QueryKeys.Invoice, { id }],
        queryFn: () => apis.engine.invoicingManagement.invoicingManagementDetail(id!),
        enabled: id != undefined,
    });

    const searchQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementSearch, { start, end, assignmentId, finalizeOnly }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementSearchList({
                from: moment(start).format("MM/DD/YYYY"),
                to: moment(end).format("MM/DD/YYYY"),
                assignmentId,
                finalizeOnly,
            }),
    });

    const includedEntriesQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementIncludedEntries, { id, categoryId }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementIncludedEntriesList({
                invoiceId: id,
                categoryId,
            }),
        enabled: id != undefined && categoryId != undefined,
    });

    const includedEntriesTotalsQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementIncludedEntriesTotals, { id, categoryId }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementIncludedEntriesTotalsList({
                invoiceId: id,
                categoryId,
            }),
        enabled: id != undefined && categoryId != undefined,
    });

    const potentialEntriesQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementPotentialEntries, { id, categoryId }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementPotentialEntriesList({
                invoiceId: id,
                categoryId,
            }),
        enabled: id != undefined && categoryId != undefined,
    });

    const potentialEntriesTotalsQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementPotentialEntriesTotals, { id, categoryId }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementPotentialEntriesTotalsList({
                invoiceId: id,
                categoryId,
            }),
        enabled: id != undefined && categoryId != undefined,
    });

    const adjustmentsQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementAdjustments, { id }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementInvoiceAdjustmentsGridList({
                invoiceId: id,
            }),
        enabled: id != undefined,
    });

    const adjustmentsTotalsQuery = useQuery({
        queryKey: [QueryKeys.InvoiceManagementAdjustmentsTotals, { id }],
        queryFn: () =>
            apis.engine.invoicingManagement.invoicingManagementInvoiceAdjustmentsGridTotalsList({
                invoiceId: id,
            }),
        enabled: id != undefined,
    });

    const finalizeInvoiceMutation = useMutation({
        mutationFn: (post: FinalizeInvoicePostModel) =>
            apis.engine.invoicingManagement.invoicingManagementFinalizeCreate(post),
        onSuccess: (_, { invoiceId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.InvoiceManagementSearch] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Invoice, { id: invoiceId }],
            });
            toast.success("Invoice finalized");
        },
        onError: (error: Error) => handleApiError(error, "Failed to finalize invoice"),
    });

    const unfinalizeInvoiceMutation = useMutation({
        mutationFn: (post: FinalizeInvoicePostModel) =>
            apis.engine.invoicingManagement.invoicingManagementUnfinalizeCreate(post),
        onSuccess: (_, { invoiceId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.InvoiceManagementSearch] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Invoice, { id: invoiceId }],
            });
            toast.success("Invoice un-finalized");
        },
        onError: (error: Error) => handleApiError(error, "Failed to un-finalize invoice"),
    });

    const deleteInvoiceMutation = useMutation({
        mutationFn: (id: number) => apis.engine.invoicingManagement.invoicingManagementDelete(id),
        onSuccess: (_, id) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.InvoiceManagementSearch] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Invoice, { id }],
            });
            toast.success("Invoice deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete invoice"),
    });

    const insertInvoiceMutation = useMutation({
        mutationFn: (post: InsertInvoicePostModel) =>
            apis.engine.invoicingManagement.invoicingManagementInsertInvoiceCreate(post),
        onSuccess: (_, id) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.InvoiceManagementSearch] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Invoice, { id }],
            });
            toast.success("Invoice saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save invoice"),
    });

    const updateInvoiceMutation = useMutation({
        mutationFn: (post: UpdateInvoicePostModel) =>
            apis.engine.invoicingManagement.invoicingManagementUpdateInvoiceCreate(post),
        onSuccess: (_, { invoiceId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.InvoiceManagementSearch] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Invoice, { id: invoiceId }],
            });
            toast.success("Invoice saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save invoice"),
    });

    const updateIncludedItemsMutation = useMutation({
        mutationFn: (post: InsertPotentialEntryPostModel) =>
            apis.engine.invoicingManagement.invoicingManagementIncludedItemsCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementIncludedEntries],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementIncludedEntriesTotals],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementPotentialEntries],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementPotentialEntriesTotals],
            });
            toast.success("Added to invoice");
        },
        onError: (error: Error) => handleApiError(error, "Failed to add to invoice"),
    });

    const createAdjustmentMutation = useMutation({
        mutationFn: (post: InsertAdjustmentPostModel) =>
            apis.engine.invoicingManagement.invoicingManagementAdjustmentItemCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementAdjustments],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementAdjustmentsTotals],
            });
            toast.success("Invoice adjustment created");
        },
        onError: (error: Error) => handleApiError(error, "Failed to create adjustment"),
    });

    const deleteAdjustmentMutation = useMutation({
        mutationFn: (id: number) => apis.engine.invoicingManagement.invoicingManagementAdjustmentItemDelete(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementAdjustments],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.InvoiceManagementAdjustmentsTotals],
            });
            toast.success("Invoice adjustment deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete adjustment"),
    });

    return {
        invoice: getInvoiceQuery,
        searchQuery,
        includedEntries: includedEntriesQuery,
        includedEntriesTotals: includedEntriesTotalsQuery,
        potentialEntries: potentialEntriesQuery,
        potentialEntriesTotals: potentialEntriesTotalsQuery,
        adjustments: adjustmentsQuery,
        adjustmentsTotals: adjustmentsTotalsQuery,
        finalizeInvoice: finalizeInvoiceMutation,
        unfinalizeInvoice: unfinalizeInvoiceMutation,
        deleteInvoice: deleteInvoiceMutation,
        insertInvoice: insertInvoiceMutation,
        updateInvoice: updateInvoiceMutation,
        updateIncludedItems: updateIncludedItemsMutation,
        createAdjustment: createAdjustmentMutation,
        deleteAdjustment: deleteAdjustmentMutation,
    };
}
